import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigationGuard } from "@/hooks/useNavigationGuard";
import { useOrgPaths } from "@/hooks/useOrgPaths";
import { useRetainedValue } from "@/hooks/useRetainedValue";
import { useToast } from "@/hooks/use-toast";
import { useExperiments, useTrial } from "@/hooks/useFirestore";
import { formatTrialDateRange } from "@/lib/utils";
import { useDeleteExperiment, useDeleteTrial } from "@/services/dataService";
import { Experiment } from "@/types/experiment";
import { Plot } from "@/types/plot";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Calendar, Edit, MapPin, MoreHorizontal, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import CopyLinkButton from "@/components/CopyLinkButton";
import TransitionOverlay from "@/components/TransitionOverlay";
import { ensureMinDuration } from "@/utils/delay";
import AddExperimentDialog from "../components/AddExperimentDialog";
import DuplicateExperimentDialog from "../components/DuplicateExperimentDialog";
import EditExperimentDialog from "../components/EditExperimentDialog";
import EditTrialDialog from "../components/EditTrialDialog";
import ExperimentList from "../components/ExperimentList";

const MIN_CREATION_TRANSITION_MS = 1500;

const TrialDetails = () => {
  const { trialId } = useParams<{ trialId: string }>();
  const { trial, loading: trialLoading, error: trialError } = useTrial(trialId);
  const { experiments, loading: experimentsLoading, error: experimentsError } = useExperiments(trialId);
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const paths = useOrgPaths();
  const beginNavigationGuard = useNavigationGuard();
  const { toast } = useToast();
  const [isAddExperimentOpen, setIsAddExperimentOpen] = useState(false);
  const [isEditTrialOpen, setIsEditTrialOpen] = useState(false);
  const [isEditExperimentOpen, setIsEditExperimentOpen] = useState(false);
  const [editingExperiment, setEditingExperiment] = useState<Experiment | null>(null);
  const [experimentEditSession, setExperimentEditSession] = useState(0);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const creationStartedAtMsRef = useRef<number>(0);
  const [creationOverlay, setCreationOverlay] = useState<{
    open: boolean;
    title: string;
    subtitle?: string;
    startedAtMs: number;
  } | null>(null);
  const [experimentSearchTerm, setExperimentSearchTerm] = useState("");
  const [duplicateSource, setDuplicateSource] = useState<{ experiment: Experiment; plots: Plot[] } | null>(null);
  const [experimentToDelete, setExperimentToDelete] = useState<Experiment | null>(null);
  const renderedDuplicateSource = useRetainedValue(duplicateSource);
  const renderedEditingExperiment = useRetainedValue(editingExperiment);
  const renderedExperimentToDelete = useRetainedValue(experimentToDelete);

  // Filter experiments based on search term
  const filteredExperiments = useMemo(() => {
    if (!experimentSearchTerm.trim()) return experiments;

    const searchLower = experimentSearchTerm.toLowerCase();
    return experiments.filter((experiment) => {
      const nameMatch = experiment.name.toLowerCase().includes(searchLower);
      const descriptionMatch = experiment.description?.toLowerCase().includes(searchLower);
      const locationMatch = experiment.location?.toLowerCase().includes(searchLower);
      return nameMatch || descriptionMatch || locationMatch;
    });
  }, [experiments, experimentSearchTerm]);

  // Role-based permissions
  // Collectors, editors, and admins can create/edit/delete (collector restrictions enforced server-side)
  const canEdit = userRole && ["collector", "editor", "admin"].includes(userRole);
  const canDelete = userRole && ["collector", "editor", "admin"].includes(userRole);

  const deleteTrial = useDeleteTrial();
  const deleteExperiment = useDeleteExperiment();

  // Show loading state
  if (trialLoading) {
    return (
      <div className="container mx-auto p-4 max-w-5xl">
        <div className="flex justify-center items-center h-64">
          <p>Loading trial...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (trialError) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">Error loading trial details: {trialError.message}</p>
      </div>
    );
  }

  // Only redirect if we're done loading and trial is still null
  if (!trialLoading && !trial) {
    return (
      <Navigate
        replace
        to={paths.home()}
      />
    );
  }

  // At this point, trial should exist
  if (!trial) {
    return null; // This shouldn't happen, but just in case
  }

  const handleExperimentCreateStart = (experimentName: string, startedAtMs: number) => {
    creationStartedAtMsRef.current = startedAtMs;
    setCreationOverlay({
      open: true,
      title: "Creating experiment...",
      subtitle: experimentName
        ? `Taking you to "${experimentName}"...`
        : "Taking you to your new experiment...",
      startedAtMs,
    });
  };

  const handleExperimentCreateError = () => {
    creationStartedAtMsRef.current = 0;
    setCreationOverlay(null);
  };

  const navigateToNewExperiment = async (experiment: Experiment, toastTitle: string, toastDescription: string) => {
    const stillHere = beginNavigationGuard();
    const startedAtMs = creationStartedAtMsRef.current || Date.now();
    await ensureMinDuration(startedAtMs, MIN_CREATION_TRANSITION_MS);
    creationStartedAtMsRef.current = 0;
    setCreationOverlay(null);

    toast({ title: toastTitle, description: toastDescription });

    setIsAddExperimentOpen(false);
    setDuplicateSource(null);
    if (stillHere()) {
      navigate(paths.experiment(trialId, experiment.id));
    }
  };

  const handleAddExperiment = (experiment: Experiment) => {
    navigateToNewExperiment(experiment, "Experiment created", "Your new experiment has been created.");
  };

  const handleExperimentDuplicated = (experiment: Experiment) => {
    navigateToNewExperiment(experiment, "Experiment duplicated", "Your experiment has been duplicated with all its plots.");
  };

  const handleEditExperiment = (experiment: Experiment) => {
    setExperimentEditSession((current) => current + 1);
    setEditingExperiment(experiment);
    setIsEditExperimentOpen(true);
  };

  const handleDuplicateExperiment = (experiment: Experiment, plots: Plot[]) => {
    setDuplicateSource({ experiment, plots });
  };

  const handleExperimentUpdated = () => {
    setIsEditExperimentOpen(false);
    setEditingExperiment(null);
  };

  const handleExperimentDeleted = () => {
    setIsEditExperimentOpen(false);
    setEditingExperiment(null);
  };

  const confirmDeleteExperiment = async (experiment: Experiment) => {
    if (!experiment.id) return;

    // Find the experiment name for the overlay message
    const experimentName = experiment.name || "experiment";

    // Show the overlay
    setCreationOverlay({
      open: true,
      title: "Deleting experiment...",
      subtitle: `Removing "${experimentName}"...`,
      startedAtMs: Date.now(),
    });

    try {
      await deleteExperiment(trialId!, experiment.id);
      toast({
        title: "Experiment deleted",
        description: "The experiment has been deleted",
      });
    } catch (error) {
      toast({
        title: "Cannot delete experiment",
        description: error instanceof Error ? error.message : "Failed to delete the experiment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCreationOverlay(null);
    }
  };

  const handleDeleteExperiment = (experiment: Experiment) => {
    setExperimentToDelete(experiment);
  };

  const handleDeleteTrial = async () => {
    // Don't manually close the dialog - let Radix's AlertDialogAction
    // handle the close via onOpenChange to ensure proper body pointer-events cleanup.
    setCreationOverlay({
      open: true,
      title: "Deleting trial...",
      subtitle: `Removing "${trial.name}"...`,
      startedAtMs: Date.now(),
    });

    const stillHere = beginNavigationGuard();
    try {
      await deleteTrial(trialId);
      toast({
        title: "Trial deleted",
        description: `${trial.name} has been deleted`,
      });
      if (stillHere()) {
        navigate(paths.home());
      }
    } catch (error) {
      toast({
        title: "Cannot delete trial",
        description: error instanceof Error ? error.message : "Failed to delete the trial. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCreationOverlay(null);
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <TransitionOverlay
        open={creationOverlay?.open ?? false}
        title={creationOverlay?.title ?? ""}
        subtitle={creationOverlay?.subtitle}
      />
      <div className="mb-4">
        <Link to={paths.home()}>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 mb-2 text-agri-green hover:text-agri-green-dark">
            <ArrowLeft className="h-4 w-4" />
            Back to Trials
          </Button>
        </Link>
      </div>

      <div className="relative">
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-2xl font-bold text-agri-green-dark">{trial.name}</CardTitle>
            <div className="flex flex-shrink-0 items-center gap-1">
              <CopyLinkButton path={paths.trial(trial.id)} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="sr-only">Open menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canEdit && (
                    <DropdownMenuItem onClick={() => setIsEditTrialOpen(true)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setIsDeleteDialogOpen(true)}
                        className="text-destructive focus:text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-foreground mb-4">{trial.description}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {trial.locationName && (
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-agri-green mr-2" />
                  <span className="text-foreground">{trial.locationName}</span>
                </div>
              )}

              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-agri-green mr-2" />
                <span className="text-foreground">{formatTrialDateRange(trial)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-agri-green">Experiments</h2>
        {canEdit && (
          <Button
            variant="default"
            onClick={() => setIsAddExperimentOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Experiment
          </Button>
        )}
      </div>

      {/* Search Box - only show if there are experiments */}
      {experiments.length > 0 && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search experiments by name, description, or location..."
            value={experimentSearchTerm}
            onChange={(e) => setExperimentSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {experimentsError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">Error loading experiments: {experimentsError.message}</p>
        </div>
      )}

      {experimentsLoading ? (
        <div className="flex justify-center items-center h-32">
          <p className="text-muted-foreground">Loading experiments...</p>
        </div>
      ) : experiments.length === 0 ? (
        <EmptyState
          title="No experiments yet"
          description="Add an experiment to start collecting data"
          buttonText="Add First Experiment"
          onAction={() => setIsAddExperimentOpen(true)}
          canEdit={canEdit}
        />
      ) : filteredExperiments.length === 0 ? (
        <EmptyState
          title="No experiments match your search"
          description="Try adjusting your search terms"
        />
      ) : (
        <ExperimentList
          experiments={filteredExperiments}
          trialId={trial.id!}
          onEditExperiment={handleEditExperiment}
          onDeleteExperiment={handleDeleteExperiment}
          onDuplicateExperiment={handleDuplicateExperiment}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      )}

      <AddExperimentDialog
        open={isAddExperimentOpen}
        onOpenChange={setIsAddExperimentOpen}
        trialId={trial.id!}
        trialName={trial.name}
        trialLocation={trial.locationName}
        onExperimentAdded={handleAddExperiment}
        onCreateStart={handleExperimentCreateStart}
        onCreateError={handleExperimentCreateError}
      />

      {renderedDuplicateSource && (
        <DuplicateExperimentDialog
          key={renderedDuplicateSource.experiment.id}
          open={duplicateSource !== null}
          onOpenChange={(open) => { if (!open) setDuplicateSource(null); }}
          trialId={trial.id!}
          sourceExperiment={renderedDuplicateSource.experiment}
          sourcePlots={renderedDuplicateSource.plots}
          onExperimentDuplicated={handleExperimentDuplicated}
          onDuplicateStart={handleExperimentCreateStart}
          onDuplicateError={handleExperimentCreateError}
        />
      )}

      <EditTrialDialog
        open={isEditTrialOpen}
        onOpenChange={setIsEditTrialOpen}
        trial={trial}
      />

      {renderedEditingExperiment && (
        <EditExperimentDialog
          key={`${renderedEditingExperiment.id}-${experimentEditSession}`}
          open={isEditExperimentOpen}
          onOpenChange={setIsEditExperimentOpen}
          experiment={renderedEditingExperiment}
          trialId={trialId!}
          onExperimentUpdated={handleExperimentUpdated}
          onExperimentDeleted={() => {
            void confirmDeleteExperiment(renderedEditingExperiment);
            handleExperimentDeleted();
          }}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      )}

      <AlertDialog
        open={experimentToDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setExperimentToDelete(null);
          }
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Experiment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{renderedExperimentToDelete?.name}"? This will permanently delete all tasks,
              plots, and data associated with this experiment. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (renderedExperimentToDelete) {
                  void confirmDeleteExperiment(renderedExperimentToDelete);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the trial "{trial.name}" and all its associated experiments, tasks, and data. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTrial}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TrialDetails;
